import {
    PaymentEndpoint as PaymentEndpointModel,
    StaticPaymentContent,
    GiftCardResponse,
    GiftCardErrorType,
} from './payment.interfaces';
import { delay } from '@shared/utils/async';
import { HttpEndpoint } from '@/api/httpEndpoints';
import { Payment } from '@/api/Cart/cart.interfaces';
import { fakeStaticPaymentContent } from '@/api/Payment/payment.fake';
import { Market } from '@shared/config/market/market.models';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';

const market: Market = getCurrentMarketSpecs();
const Currency = market.moneyFormat.currency;

export interface AdyenMoney {
    currency: string;
    value: number;
}

export class PaymentEndpoint extends HttpEndpoint implements PaymentEndpointModel {
    public constructor(applicationUrl: string) {
        super(applicationUrl);
    }
    public async GetPaymentMethods(amount: AdyenMoney): Promise<any> {
        return await this.post('mvc/Adyen/GetPaymentMethods', amount);
    }
    public async SubmitPayment(adyenData: any, cartPayment: Payment): Promise<any> {
        return await this.post('mvc/Adyen/RequestPayment', { adyenData, cartPayment });
    }
    public async GetDetails(adyenData: any): Promise<any> {
        return await this.post('mvc/Adyen/GetDetails', adyenData);
    }
    public async GetPaymentContent(bookingToken: string): Promise<StaticPaymentContent> {
        return this.get(
            this.buildPathWithQuery('mvc/Payment/GetStaticPaymentPageContent', {
                bookingToken,
            })
        );
    }

    public async RedeemGiftCard(
        giftCardCode: string,
        cvc: string | undefined,
        isPartPayment: boolean,
        bookingToken: string | null
    ): Promise<GiftCardResponse> {
        return this.post('mvc/GiftCard/RedeemGiftCard', {
            isPartPayment,
            bookingToken,
            giftCardCode,
            cvc,
        });
    }
}

export class FakePaymentEndpoint extends HttpEndpoint implements PaymentEndpoint {
    public async GetPaymentMethods(): Promise<any> {
        return {
            paymentMethods: [
                { name: 'Trustly', type: 'trustly' },
                { name: 'Swish', type: 'swish' },
                { name: 'Klarna', type: 'klarna' },
                {
                    brands: ['visa', 'mc'],
                    name: 'Kortbetalning',
                    type: 'scheme',
                },
            ],
        };
    }
    public async SubmitPayment(): Promise<any> {
        await delay(500);
        throw new Error('Method not implemented.');
    }
    public async GetDetails(): Promise<any> {
        await delay(500);
        throw new Error('Method not implemented.');
    }

    public async GetPaymentContent(): Promise<StaticPaymentContent> {
        await delay(500);
        return fakeStaticPaymentContent;
    }

    public async RedeemGiftCard(
        giftCardCode: string,
        cvc: string | undefined
    ): Promise<GiftCardResponse> {
        const validGiftCards = [
            {
                Code: 'cvc',
                Value: {
                    Amount: 500,
                    Currency,
                },
            },
            {
                Code: 'nobalance',
                Value: {
                    Amount: 500,
                    Currency,
                },
            },
            {
                Code: 'sembo',
                Value: {
                    Amount: 166,
                    Currency,
                },
            },
            {
                Code: 'notsupported',
                Value: {
                    Amount: 250,
                    Currency,
                },
            },
            {
                Code: 'holidays',
                Value: {
                    Amount: 420,
                    Currency,
                },
            },
        ];

        const validCode = validGiftCards.find((c) => c.Code === giftCardCode);
        await delay(1000);

        if (validCode === undefined) {
            return {
                Balance: null,
                Error: GiftCardErrorType.Invalid,
            };
        }

        if (validCode.Code === 'cvc') {
            if (cvc) {
                if (cvc === '1234') {
                    // Valid code and cvc
                    return {
                        Balance: validCode.Value,
                        Error: null,
                    };
                } else {
                    // Invalid cvc code
                    return {
                        Balance: null,
                        Error: GiftCardErrorType.IncorrectCvc,
                    };
                }
            } else {
                return {
                    Balance: null,
                    Error: GiftCardErrorType.RequiresCvc,
                };
            }
        }

        if (validCode.Code === 'nobalance') {
            return {
                Balance: null,
                Error: GiftCardErrorType.NoBalance,
            };
        }
        if (validCode.Code === 'notsupported') {
            return {
                Balance: null,
                Error: GiftCardErrorType.ProductNotSupported,
            };
        }
        return {
            Balance: validCode.Value,
            Error: null,
        };
    }
}
